import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { phappsRequest } from "@/services/apiService";
import { PHARMACY_HALO_COLORS } from "@/config/colors";
import { API_PATHS } from "@/config/apiConstants";
import PharmacyDataGrid from "@/components/tables/shared/PharmacyDataGrid";
import PingAppTeamModal from "@/components/modals/PingAppTeamModal";
import useAlert from "@/components/feedback/useAlert";

const AppNames = {
  Quda: 1,
  MediDrop: 2,
  Common: 3,
  Webbit: 4,
  Hub: 5,
  HaloPay: 6,
  Zephyr: 7,
  HaloText: 8,
  DocLink: 9,
  Cherub: 10
};

const Subscriptions = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Ping Modal State
  const [pingModalOpen, setPingModalOpen] = useState(false);
  const [selectedPingData, setSelectedPingData] = useState(null);
  const { showAlert, AlertSnackbar } = useAlert();

  const handlePingRequest = (row, appName) => {
    setSelectedPingData({ row, appName });
    setPingModalOpen(true);
  };

  const handleConfirmPing = async () => {
    if (!selectedPingData) return;

    try {
      const { row, appName } = selectedPingData;
      const appID = AppNames[appName];

      if (!appID) {
        console.error("Invalid App Name:", appName);
        return;
      }

      if (!row.chemistID) {
        console.error("Missing ChemistID/PHUUID for row:", row);
        showAlert({
          severity: "error",
          title: "Error",
          message: "Cannot identify chemist (Missing PHUUID).",
        });
        return;
      }

      const payload = {
        AppID: appID,
        PHUUID: row.chemistID,
      };

      console.log("Sending PingAppTeam request:", payload);

      await phappsRequest(API_PATHS.PING_APP_TEAM, "POST", payload, payload);

      setPingModalOpen(false);
      setSelectedPingData(null);
      showAlert({
        severity: "success",
        title: "Success",
        message: "Request sent successfully!",
      });
    } catch (err) {
      console.error("Error sending ping request:", err);
      showAlert({
        severity: "error",
        title: "Error",
        message: "Failed to send request. Please try again.",
      });
    }
  };

  React.useEffect(() => {
    const fetchSubs = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await phappsRequest(API_PATHS.CHEMIST_SUBS, "GET");
        if (response && Array.isArray(response)) {
          setData(response);
        } else if (response && response.ChemistSubs) {
          setData(response.ChemistSubs);
        } else {
          setData([]);
        }
      } catch (err) {
        console.error("Error fetching ChemistSubs:", err);
        setError("Error fetching subscription data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchSubs();
  }, []);


  const subsData = useMemo(
    () => ({
      MainData: {
        pharmacyData: data.map((item) => ({
          chemistID: item.PHUUID || item.ChemistID,
          pharmacyName: item.ChemistName,
          // Add dummy values for other fields required by formatPharmacyRow in PharmacyDataGrid
          // or meaningful ones if available.
          qudaStats: {},
          waitingListStats: {},
          doclinkStats: {},
          // Custom fields for subscriptions
          QudaSubStatus: item.QudaSubStatus,
          DocLinkSubStatus: item.DocLinkSubStatus,
        })),
      },
    }),
    [data]
  );

  const statusSummary = useMemo(() => {
    const normalize = (status) =>
      (status || "").toString().trim().toLowerCase();
    const isTrialing = (status) => normalize(status).includes("trial"); // covers "trial" + "trialing"

    return data.reduce(
      (acc, item) => {
        const qudaStatus = normalize(item.QudaSubStatus);
        const docStatus = normalize(item.DocLinkSubStatus);

        if (qudaStatus === "active") acc.quda.active += 1;
        else if (isTrialing(qudaStatus)) acc.quda.trialing += 1;

        if (docStatus === "active") acc.doclink.active += 1;
        else if (isTrialing(docStatus)) acc.doclink.trialing += 1;

        return acc;
      },
      {
        quda: { active: 0, trialing: 0 },
        doclink: { active: 0, trialing: 0 },
      }
    );
  }, [data]);

  if (loading) {
    return (
      <Box className="flex items-center justify-center h-64">
        <Box className="text-center">
          <CircularProgress
            size={60}
            sx={{ color: PHARMACY_HALO_COLORS.BLUE, mb: 2 }}
          />
          <Typography variant="h6" color="textSecondary">
            Loading subscriptions...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex items-center justify-center h-64">
        <Alert
          severity="error"
          sx={{
            maxWidth: 600,
            "& .MuiAlert-icon": { color: PHARMACY_HALO_COLORS.RED },
          }}
        >
          <Typography variant="h6" className="mb-2">
            {error}
          </Typography>
          <Typography variant="body2">
            Please check your connection and try again.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box className="space-y-4">
      <Typography
        variant="h4"
        className="font-bold mb-6"
        style={{ color: PHARMACY_HALO_COLORS.BLUE }}
      >
        Subscriptions
      </Typography>

      <Box className="grid gap-4 md:grid-cols-2">
        {[
          {
            label: "Quda",
            counts: statusSummary.quda,
            accent: PHARMACY_HALO_COLORS.BLUE,
          },
          {
            label: "DocLink",
            counts: statusSummary.doclink,
            accent: PHARMACY_HALO_COLORS.RED,
          },
        ].map(({ label, counts, accent }) => (
          <Box
            key={label}
            sx={{
              border: `2px solid ${accent}`,
              borderRadius: 2,
              p: 3,
              backgroundColor: "#fff",
              boxShadow: 1,
            }}
          >
            <Typography variant="h6" sx={{ color: accent, fontWeight: 700 }}>
              {label} Subscriptions
            </Typography>
            <Divider sx={{ my: 2, borderColor: `${accent}33` }} />
            <Box className="flex justify-between">
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Active
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {counts.active}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Trialing
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {counts.trialing}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* 
        We need to pass a custom prop to PharmacyDataGrid to tell it to use different columns 
        and skip the stats summary if possible. 
        Currently PharmacyDataGrid is very specific. 
        I will modify PharmacyDataGrid to accept `customColumns` and `customRowTransformer`.
      */}
      <PharmacyDataGrid 
        data={subsData} 
        isSubscriptionMode={true} 
        onPingAppTeam={handlePingRequest}
      />
      
      <PingAppTeamModal
        open={pingModalOpen}
        onClose={() => setPingModalOpen(false)}
        onConfirm={handleConfirmPing}
        appName={selectedPingData?.appName}
      />

      {AlertSnackbar}
    </Box>
  );
};

export default Subscriptions;
